import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

type ParticleSphereAnimationProps = {
  className?: string;
};

/** Lightweight rotating particle sphere for the MFA OTP visual. */
export default function ParticleSphereAnimation({ className }: ParticleSphereAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let running = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const size = 160;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const particles = Array.from({ length: 90 }, (_, i) => {
      const phi = Math.acos(-1 + (2 * i) / 90);
      const theta = Math.sqrt(90 * Math.PI) * phi;
      return {
        x: Math.cos(theta) * Math.sin(phi),
        y: Math.sin(theta) * Math.sin(phi),
        z: Math.cos(phi),
      };
    });

    let angle = 0;
    const draw = () => {
      if (!running) return;
      angle += 0.008;
      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const radius = size * 0.34;

      for (const p of particles) {
        const x1 = p.x * Math.cos(angle) - p.z * Math.sin(angle);
        const z1 = p.x * Math.sin(angle) + p.z * Math.cos(angle);
        const y1 = p.y;
        const depth = (z1 + 1.4) / 2.4;
        const px = cx + x1 * radius;
        const py = cy + y1 * radius;
        const alpha = 0.25 + depth * 0.75;
        const r = 1.1 + depth * 1.8;
        ctx.beginPath();
        ctx.fillStyle = `rgba(59, 130, 246, ${alpha.toFixed(3)})`;
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = window.requestAnimationFrame(draw);
    };

    draw();
    return () => {
      running = false;
      window.cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={cn('pointer-events-none', className)}
      aria-hidden
    />
  );
}
